import { ReactNode } from "react";
import { Folder } from "@phosphor-icons/react";
import { RichInput } from "@reactive-resume/ui";
import { createId } from "@paralleldrive/cuid2";
import { defaultCustomSection } from "@reactive-resume/schema";

import { useCoverLetterStore } from "@/client/stores/cover-letter";
import { CLCollapsibleSection } from "../../../CollapsibleSection";
import { CLCustomSectionOptions } from "./section-options";

type Props = {
  id: string; // customId e.g. "cuid"
};

export const CLCustomSection = ({ id }: Props) => {
  const setValue = useCoverLetterStore((state) => state.setValue);
  const section = useCoverLetterStore((state) => state.coverLetter?.data?.custom?.[id]);

  if (!section) return null;

  const content =
    section.content ??
    section.items?.[0]?.summary ??
    "";

  const onContentChange = (value: string) => {
    setValue(`custom.${id}.content`, value);
    const firstItem = section.items?.[0] || {
      ...defaultCustomSection,
      id: createId(),
      name: "",
    };
    setValue(`custom.${id}.items`, [
      {
        ...firstItem,
        summary: value,
        visible: true,
      },
    ]);
  };

  const headerHTML: ReactNode = (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center gap-x-4">
        <Folder size={18} />
        <h2 className="line-clamp-1 text-base font-bold text-gray-800 lg:text-lg">
          {section.name || "Custom Section"}
        </h2>
      </div>

      <CLCustomSectionOptions id={id} />
    </div>
  );

  const contentHTML: ReactNode = (
    <div className="space-y-2">
      <RichInput
        content={content}
        onChange={onContentChange}
      />
    </div>
  );

  return (
    <CLCollapsibleSection
      id={`cl-custom-${id}`}
      header={headerHTML}
      defaultOpen={true}
      content={contentHTML}
    />
  );
};
